import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { CallerService } from '../services/caller.service'
import { AccommodationType, AppState } from '../ngrx-store/app.state'
import { selectFeatureFeed, selectFeedView } from '../ngrx-store/selectors/corp.selectors'
import { loadAllItems, loadAllItemsFailure } from '../ngrx-store/actions/corp-member.actions'
import { ACCOMMODATION_TYPES } from '../utils/constants.util'
import { locationState, nearness } from '../utils/sale.util'
import { accommodationTypeLabel } from '../utils/accommodation.util'

/** The query params the filters live in, so a filtered page can be shared or bookmarked. */
const WHAT_PARAM = 'type'
const STATE_PARAM = 'state'

/**
 * "What" is one dropdown for the two things corpers look for most - a free lodge, a room to share - and then
 * each type of house. The first two aren't types, so they can't clash with one.
 */
const FREE = 'free'
const ROOMMATE = 'roommate'
const SPECIAL_LABELS: Record<string, string> = { [FREE]: 'Free lodges', [ROOMMATE]: 'Seeking a roommate' }

/** Types in the order the posting form lists them; any other (older) type after them. */
const TYPE_ORDER: string[] = ACCOMMODATION_TYPES.map((type) => type.toLowerCase())

interface HouseFilters {
  special: Array<{ value: string; label: string; count: number }>
  types: Array<{ value: string; label: string; count: number }>
  states: Array<{ value: string; count: number }>
  allWhat: number // "All houses": everything in the chosen state
  allStates: number // "All states": everything matching "what"
  filtered: boolean
}

const stateOf = (house: AccommodationType): string | undefined => locationState(house.CorpMember?._location)

/** Types are saved as typed, so 'Self contain' and 'self contain' are one. */
const typeOf = (house: AccommodationType): string | null => house.accommodation_type?.trim().toLowerCase() || null

const typeLabel = accommodationTypeLabel

function matchesWhat(house: AccommodationType, what: string | null): boolean {
  if (!what) return true
  if (what === FREE) return house.rent_interval === 'free'
  if (what === ROOMMATE) return house.status === 'seeking_roommate'
  return typeOf(house) === what
}

@Component({
  selector: 'app-houses-page',
  templateUrl: './houses-page.component.html',
  styleUrls: ['./houses-page.component.css'],
})
export class HousesPageComponent implements OnInit, OnDestroy {
  what$ = new BehaviorSubject<string | null>(null)
  state$ = new BehaviorSubject<string | null>(null)

  /** Every house in the feed, newest first. */
  private houses$: Observable<AccommodationType[]> = this.store
    .select(selectFeedView('accommodation'))
    .pipe(map((houses) => [...houses.values()] as AccommodationType[]))

  /**
   * The two dropdowns' options, as on /sales: each says how many houses it would show given the other filter,
   * and one with nothing is left out - except the one that's picked, so it never vanishes from under you.
   */
  filters$: Observable<HouseFilters> = combineLatest([this.houses$, this.what$, this.state$]).pipe(
    map(([houses, what, state]) => {
      const inState = houses.filter((house) => !state || stateOf(house) === state)
      const matching = houses.filter((house) => matchesWhat(house, what))
      const count = (value: string) => inState.filter((house) => matchesWhat(house, value)).length
      const kept = (option: { value: string; count: number }) => option.count > 0 || option.value === what

      const types = new Set(inState.map(typeOf).filter((type): type is string => !!type))
      if (what && !SPECIAL_LABELS[what]) types.add(what)
      const rank = (type: string) => (TYPE_ORDER.includes(type) ? TYPE_ORDER.indexOf(type) : TYPE_ORDER.length)

      const stateCounts = new Map<string, number>()
      for (const house of matching) {
        const s = stateOf(house)
        if (s) stateCounts.set(s, (stateCounts.get(s) ?? 0) + 1)
      }
      if (state && !stateCounts.has(state)) stateCounts.set(state, 0)

      return {
        special: [FREE, ROOMMATE].map((value) => ({ value, label: SPECIAL_LABELS[value], count: count(value) })).filter(kept),
        types: [...types]
          .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
          .map((value) => ({ value, label: typeLabel(value), count: count(value) }))
          .filter(kept),
        states: [...stateCounts].map(([value, count]) => ({ value, count })).sort((a, b) => a.value.localeCompare(b.value)),
        allWhat: inState.length,
        allStates: matching.length,
        filtered: !!(what || state),
      }
    }),
  )

  /** What the list shows: the filters applied, nearest to you first (same LGA, then same state). */
  shown$: Observable<AccommodationType[]> = combineLatest([this.houses$, this.what$, this.state$]).pipe(
    map(([houses, what, state]) => {
      const viewer = this.callerService.corpMember?._location
      return houses
        .filter((house) => matchesWhat(house, what) && (!state || stateOf(house) === state))
        // Array.sort is stable, so each group stays newest first.
        .sort((a, b) => nearness(b.CorpMember?._location, viewer) - nearness(a.CorpMember?._location, viewer))
    }),
  )

  hasAnyHouses$: Observable<boolean> = this.houses$.pipe(map((houses) => houses.length > 0))

  /** Until GET /all-items first answers (or fails). */
  isFetching = true

  private destroy$ = new Subject<void>()

  constructor(
    public callerService: CallerService,
    public store: Store<AppState>,
    private actions$: Actions,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    this.store.dispatch(loadAllItems())

    this.store.select(selectFeatureFeed).pipe(takeUntil(this.destroy$)).subscribe((feed) => {
      if (feed.lastFetchedAt || feed.accommodations.size) this.isFetching = false
    })
    this.actions$.pipe(ofType(loadAllItemsFailure), takeUntil(this.destroy$)).subscribe(() => {
      this.isFetching = false
      this.callerService.showNotification('Couldn\'t load houses. Check your connection and try again.')
    })

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.what$.next(params.get(WHAT_PARAM))
      this.state$.next(params.get(STATE_PARAM))
    })
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /** The "what" dropdown's closed text. */
  whatName(what: string | null): string {
    if (!what) return 'All houses'
    return SPECIAL_LABELS[what] ?? typeLabel(what)
  }

  setWhat(what: string | null) {
    this.setFilter(WHAT_PARAM, what)
  }

  setState(state: string | null) {
    this.setFilter(STATE_PARAM, state)
  }

  clearFilters() {
    this.router.navigate([], { relativeTo: this.route, queryParams: { [WHAT_PARAM]: null, [STATE_PARAM]: null }, queryParamsHandling: 'merge', replaceUrl: true })
  }

  private setFilter(param: string, value: string | null) {
    this.router.navigate([], { relativeTo: this.route, queryParams: { [param]: value }, queryParamsHandling: 'merge', replaceUrl: true })
  }

  openWishlist() {
    this.callerService.openOnboardingChecklist().subscribe()
  }

  trackById = (_index: number, house: AccommodationType) => house.id
}
